import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useToast, ToastView } from "../components/ui";
import RowActionsMenu, { IconOpen } from "../components/RowActionsMenu";

interface OrgUser {
  id: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  last_login_at: string | null;
}

// ── Tipos ────────────────────────────────────────────────────────────────────

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: "core" | "pro" | "ultra";
  is_active: boolean;
  max_users: number;
  go_live_at?: string | null;
  contact_name: string | null;
  contact_email: string | null;
  ccaa?: string | null;
  municipio?: string | null;
  notes: string | null;
  primary_color: string | null;
  logo_url: string | null;
  require_2fa: boolean;
  created_at: string;
  updated_at: string;
  user_count: number;
}

interface SetupResult {
  loaded: number;
  skipped: number;
  lab_names_used: string[];
  not_found_in_catalog: string[];
  catalog_size: number;
}

// ── Constantes de estilo ──────────────────────────────────────────────────────

const PLAN_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  core:  { bg: "#3b82f620", color: "#3b82f6", label: "Core"  },
  pro:   { bg: "#8b5cf620", color: "#8b5cf6", label: "Pro"   },
  ultra: { bg: "#f59e0b20", color: "#f59e0b", label: "Ultra" },
};

const card: React.CSSProperties = {
  background: "var(--clr-surface)",
  backdropFilter: "var(--clr-blur)",
  WebkitBackdropFilter: "var(--clr-blur)",
  border: "1px solid var(--clr-border)",
  borderRadius: 18,
  boxShadow: "var(--clr-shadow-sm)",
  padding: "1.25rem 1.5rem",
};

const btn = (variant: "primary" | "ghost" | "danger" = "primary"): React.CSSProperties => ({
  padding: "0.5rem 1.1rem",
  borderRadius: 999,
  border: variant === "primary" ? "none" : "1px solid var(--clr-border)",
  background: variant === "primary" ? "var(--clr-org-primary)" : "var(--clr-surface)",
  color: variant === "primary" ? "#fff" : variant === "danger" ? "#ef4444" : "var(--clr-text)",
  fontWeight: 600,
  fontSize: "0.82rem",
  cursor: "pointer",
  transition: "opacity 0.15s",
});

const input: React.CSSProperties = {
  width: "100%",
  padding: "0.55rem 0.875rem",
  borderRadius: 12,
  border: "1px solid var(--clr-border)",
  background: "var(--clr-input-bg)",
  color: "var(--clr-text)",
  fontSize: "0.875rem",
  outline: "none",
  boxSizing: "border-box",
};

// ── Estado: punto semántico + etiqueta neutra (v3) ───────────────────────────

function StateDot({ active, label }: { active: boolean; label: string }) {
  const color = active ? "#22c55e" : "#ef4444";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: color + "1e", color,
      borderRadius: 999, padding: "3px 11px",
      fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: color, display: "inline-block", flexShrink: 0 }} />
      {label}
    </span>
  );
}

// ── PlanBadge ────────────────────────────────────────────────────────────────

function PlanBadge({ plan }: { plan: string }) {
  const s = PLAN_STYLE[plan] ?? { bg: "#6b728020", color: "#6b7280", label: plan };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 10px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase" }}>
      {s.label}
    </span>
  );
}

// Domicilio del laboratorio para los plazos (Ley 39/2015 art. 30.6): sus festivos
// autonómicos y locales pueden alargar un plazo → «posible prórroga» (catálogo 4.1).
const CCAA: [string, string][] = [
  ["AN", "Andalucía"], ["AR", "Aragón"], ["AS", "Asturias"], ["IB", "Illes Balears"], ["CN", "Canarias"],
  ["CB", "Cantabria"], ["CM", "Castilla-La Mancha"], ["CL", "Castilla y León"], ["CT", "Cataluña"],
  ["EX", "Extremadura"], ["GA", "Galicia"], ["MD", "Comunidad de Madrid"], ["MC", "Región de Murcia"],
  ["NC", "C. Foral de Navarra"], ["PV", "País Vasco"], ["RI", "La Rioja"], ["VC", "Comunitat Valenciana"],
  ["CE", "Ceuta"], ["ML", "Melilla"],
];

function DomicilioFields({ ccaa, municipio, onChange }: {
  ccaa: string; municipio: string; onChange: (ccaa: string, municipio: string) => void;
}) {
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Comunidad autónoma</label>
          <select style={{ ...input }} value={ccaa} onChange={e => onChange(e.target.value, municipio)}>
            <option value="">—</option>
            {CCAA.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Municipio</label>
          <input style={input} value={municipio} onChange={e => onChange(ccaa, e.target.value)} placeholder="Barcelona" />
        </div>
      </div>
      <div style={{ fontSize: "0.72rem", color: "var(--clr-text3)", marginTop: 4 }}>
        Domicilio del laboratorio: sus festivos pueden alargar plazos (se avisa como «posible prórroga»).
      </div>
    </div>
  );
}

// ── Modal: Crear organización ─────────────────────────────────────────────────

function CreateOrgModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: "", slug: "", plan: "core", contact_name: "", contact_email: "", ccaa: "", municipio: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const autoSlug = (name: string) =>
    name.toLowerCase().replace(/[áàä]/g, "a").replace(/[éèë]/g, "e").replace(/[íìï]/g, "i")
      .replace(/[óòö]/g, "o").replace(/[úùü]/g, "u").replace(/ñ/g, "n")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const handleName = (v: string) => setForm(f => ({ ...f, name: v, slug: autoSlug(v) }));

  const submit = async () => {
    if (!form.name || !form.slug) { setError("Nombre y slug son obligatorios"); return; }
    setLoading(true); setError("");
    try {
      await api.post("/organizations", form);
      onCreated();
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
      <div style={{ ...card, background: "var(--clr-modal-solid)", borderRadius: 22, boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)", width: 480, maxWidth: "90vw" }}>
        <h3 style={{ margin: "0 0 1.25rem", fontSize: "1.05rem", fontWeight: 600, letterSpacing: "-0.01em" }}>Nueva organización</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Nombre *</label>
            <input style={input} value={form.name} onChange={e => handleName(e.target.value)} placeholder="Almirall S.A." />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Slug * (URL único)</label>
            <input style={input} value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="almirall" />
          </div>
          <div>
            <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Plan</label>
            <select style={{ ...input }} value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
              <option value="core">Core — 5 usuarios</option>
              <option value="pro">Pro — 10 usuarios</option>
              <option value="ultra">Ultra — 20 usuarios</option>
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Contacto</label>
              <input style={input} value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="Nombre del contacto" />
            </div>
            <div>
              <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Email</label>
              <input style={input} type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="contacto@empresa.es" />
            </div>
          </div>
          <DomicilioFields ccaa={form.ccaa} municipio={form.municipio}
            onChange={(ccaa, municipio) => setForm(f => ({ ...f, ccaa, municipio }))} />
          <div>
            <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Notas internas (HelixScan)</label>
            <textarea style={{ ...input, resize: "vertical", minHeight: 72 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Condiciones negociadas, fecha de inicio, observaciones..." />
          </div>
        </div>

        {error && <p style={{ color: "#ef4444", fontSize: "0.8rem", margin: "0.75rem 0 0" }}>{error}</p>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: "1.25rem" }}>
          <button style={btn("ghost")} onClick={onClose}>Cancelar</button>
          <button style={btn()} onClick={submit} disabled={loading}>{loading ? "Creando…" : "Crear organización"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Panel: Gestionar organización ─────────────────────────────────────────────

function ManageOrgPanel({ org, onClose, onUpdated }: { org: Organization; onClose: () => void; onUpdated: () => void }) {
  const [labInput, setLabInput] = useState("");
  const [labNames, setLabNames] = useState<string[]>([]);
  const [setupResult, setSetupResult] = useState<SetupResult | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState("");
  const [editPlan, setEditPlan] = useState(org.plan);
  // Cuentas activas como máximo (contrato con Italfarmaco: cinco); por defecto, el del plan.
  const [editMaxUsers, setEditMaxUsers] = useState<number>(org.max_users);
  const [editNotes, setEditNotes] = useState(org.notes ?? "");
  const [edit2fa, setEdit2fa] = useState(!!org.require_2fa);
  const [editCcaa, setEditCcaa] = useState(org.ccaa ?? "");
  const [editMunicipio, setEditMunicipio] = useState(org.municipio ?? "");
  // Puesta en producción (acta de go-live): de ahí cuentan los 30 días del Informe de Operación Inicial.
  const toLocal = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const [editGoLive, setEditGoLive] = useState(toLocal(org.go_live_at));
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"info" | "portfolio" | "usuarios" | "branding">("info");
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  // Branding state
  const [brandColor, setBrandColor] = useState(org.primary_color || "#0891b2");
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(org.logo_url || null);
  const [brandLogoFile, setBrandLogoFile] = useState<File | null>(null);
  const [brandLogoPreview, setBrandLogoPreview] = useState<string | null>(org.logo_url || null);
  const [brandSaving, setBrandSaving] = useState(false);
  const [brandUploading, setBrandUploading] = useState(false);
  const [brandSuccess, setBrandSuccess] = useState(false);
  const [brandError, setBrandError] = useState("");
  const [brandDragOver, setBrandDragOver] = useState(false);
  const brandFileRef = useRef<HTMLInputElement>(null);
  const startImpersonation = useAuthStore(s => s.startImpersonation);
  const navigate = useNavigate();
  const { toast, showToast } = useToast();

  useEffect(() => {
    if (activeTab !== "usuarios") return;
    setUsersLoading(true);
    api.get<OrgUser[]>(`/organizations/${org.id}/users`)
      .then(setUsers)
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, [activeTab, org.id]);

  const handleImpersonate = async (user: OrgUser) => {
    setImpersonatingId(user.id);
    try {
      const data = await api.post<{
        access_token: string; refresh_token: string; role: string; email: string;
        first_name: string | null; last_name: string | null;
        organization_id: string | null; organization_name: string | null; plan: string | null;
        primary_color: string | null; logo_url: string | null;
      }>(`/auth/impersonate/${user.id}`, {});
      startImpersonation(data.access_token, data.refresh_token, {
        email: data.email, role: data.role,
        first_name: data.first_name, last_name: data.last_name,
        organization_id: data.organization_id, organization_name: data.organization_name, plan: data.plan,
        primary_color: data.primary_color, logo_url: data.logo_url,
      });
      onClose();
      navigate("/dashboard");
    } catch (e: any) {
      showToast(`Error al impersonar: ${e.message}`, false);
    } finally {
      setImpersonatingId(null);
    }
  };

  const handleBrandFile = (file: File) => {
    if (!file.type.startsWith("image/")) { setBrandError("Solo imágenes (PNG, JPG, WebP, SVG)"); return; }
    if (file.size > 2 * 1024 * 1024) { setBrandError("Máximo 2 MB"); return; }
    setBrandError(""); setBrandLogoFile(file); setBrandLogoPreview(URL.createObjectURL(file));
  };

  const saveBrandColor = async () => {
    setBrandSaving(true); setBrandError(""); setBrandSuccess(false);
    try {
      await api.put(`/organizations/${org.id}/branding`, { primary_color: brandColor });
      setBrandSuccess(true); setTimeout(() => setBrandSuccess(false), 3000);
      onUpdated();
    } catch (e: any) { setBrandError(e.message); }
    finally { setBrandSaving(false); }
  };

  const uploadBrandLogo = async () => {
    if (!brandLogoFile) return;
    setBrandUploading(true); setBrandError(""); setBrandSuccess(false);
    try {
      const fd = new FormData(); fd.append("file", brandLogoFile);
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(`/api/organizations/${org.id}/logo`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Error al subir"); }
      const data = await res.json();
      setBrandLogoUrl(data.logo_url); setBrandLogoPreview(data.logo_url); setBrandLogoFile(null);
      setBrandSuccess(true); setTimeout(() => setBrandSuccess(false), 3000);
      onUpdated();
    } catch (e: any) { setBrandError(e.message); }
    finally { setBrandUploading(false); }
  };

  const deleteBrandLogo = async () => {
    setBrandSaving(true); setBrandError("");
    try {
      await api.delete(`/organizations/${org.id}/logo`);
      setBrandLogoUrl(null); setBrandLogoPreview(null); setBrandLogoFile(null);
      onUpdated();
    } catch (e: any) { setBrandError(e.message); }
    finally { setBrandSaving(false); }
  };

  const PRESET_COLORS = ["#0891b2","#1d4ed8","#7c3aed","#db2777","#ea580c","#0891b2","#059669","#dc2626","#92400e","#0f172a"];

  const addLab = () => {
    const v = labInput.trim();
    if (v && !labNames.includes(v)) setLabNames(l => [...l, v]);
    setLabInput("");
  };

  const runSetup = async () => {
    if (!labNames.length) { setSetupError("Añade al menos un nombre de laboratorio"); return; }
    setSetupLoading(true); setSetupError(""); setSetupResult(null);
    try {
      const r = await api.post<SetupResult>(`/organizations/${org.id}/setup-portfolio`, { lab_names: labNames });
      setSetupResult(r);
      onUpdated();
    } catch (e: any) { setSetupError(e.message); }
    finally { setSetupLoading(false); }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await api.put(`/organizations/${org.id}`, { plan: editPlan, notes: editNotes, require_2fa: edit2fa,
                                                  ccaa: editCcaa, municipio: editMunicipio,
                                                  ...(editMaxUsers !== org.max_users ? { max_users: editMaxUsers } : {}),
                                                  ...(editGoLive !== toLocal(org.go_live_at)
                                                    ? { go_live_at: editGoLive ? new Date(editGoLive).toISOString() : null } : {}) });
      onUpdated();
    } catch {}
    finally { setSaving(false); }
  };

  const TAB = (active: boolean): React.CSSProperties => ({
    padding: "0.42rem 1rem", borderRadius: 999,
    border: active ? "1px solid transparent" : "1px solid var(--clr-border)",
    background: active ? "var(--clr-org-primary)" : "var(--clr-surface)",
    color: active ? "#fff" : "var(--clr-text3)",
    fontWeight: active ? 600 : 500,
    fontSize: "0.78rem", cursor: "pointer", transition: "all 0.15s",
  });

  const TAB_LABELS: Record<"info" | "usuarios" | "branding" | "portfolio", string> = {
    info: "Info", usuarios: "Usuarios", branding: "Branding", portfolio: "Portfolio",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
      <ToastView toast={toast} />
      <div style={{ ...card, background: "var(--clr-modal-solid)", borderRadius: 22, boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)", width: 560, maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto", fontVariantNumeric: "tabular-nums" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1rem" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 600, letterSpacing: "-0.01em" }}>{org.name}</h3>
            <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
              <PlanBadge plan={org.plan} />
              <span style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>/{org.slug}</span>
              {!org.is_active && <StateDot active={false} label="Inactiva" />}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              width: 30, height: 30, borderRadius: 15,
              background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)",
              color: "var(--clr-text3)", cursor: "pointer", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.8rem", lineHeight: 1,
            }}
          >✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: "1.25rem", flexWrap: "wrap" }}>
          {(["info", "usuarios", "branding", "portfolio"] as const).map(t => (
            <button key={t} style={TAB(activeTab === t)} onClick={() => setActiveTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>

        {/* Tab: Información */}
        {activeTab === "info" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Franja de datos: separador fino, números en tinta */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "0.25rem 0 0.75rem", borderBottom: "1px solid var(--clr-border2)" }}>
              <div>
                <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 5 }}>Usuarios</div>
                <div style={{ fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1 }}>
                  {org.user_count.toLocaleString("es-ES")} <span style={{ fontSize: "0.8rem", fontWeight: 400, color: "var(--clr-text3)" }}>/ {org.max_users.toLocaleString("es-ES")}</span>
                </div>
              </div>
              <div style={{ paddingLeft: 16, borderLeft: "1px solid var(--clr-border2)" }}>
                <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 5 }}>Contacto</div>
                <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--clr-text)" }}>{org.contact_name || "—"}</div>
                <div style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>{org.contact_email || "—"}</div>
              </div>
            </div>

            <div>
              <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Plan</label>
              <select style={{ ...input }} value={editPlan} onChange={e => setEditPlan(e.target.value as any)}>
                <option value="core">Core — 5 usuarios</option>
                <option value="pro">Pro — 10 usuarios</option>
                <option value="ultra">Ultra — 20 usuarios</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Cuentas activas como máximo</label>
              <input type="number" min={1} max={500} style={{ ...input, maxWidth: 140 }} value={editMaxUsers}
                onChange={e => setEditMaxUsers(Math.max(1, Number(e.target.value) || 1))} />
              <span style={{ fontSize: "0.72rem", color: "var(--clr-text3)", marginLeft: 10 }}>
                Por encima no se puede crear ni activar otra cuenta: hay que sustituir o dar de baja una.
              </span>
            </div>

            <div>
              <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Puesta en producción (go-live)</label>
              <input type="datetime-local" style={{ ...input, maxWidth: 220 }} value={editGoLive}
                onChange={e => setEditGoLive(e.target.value)} aria-label="Puesta en producción" />
              <span style={{ fontSize: "0.72rem", color: "var(--clr-text3)", marginLeft: 10 }}>
                La del acta de go-live. De ahí cuentan los treinta días del Informe de Operación Inicial.
              </span>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "0.75rem 1rem", border: "1px solid var(--clr-border)", borderRadius: 12 }}>
              <input type="checkbox" checked={edit2fa} onChange={e => setEdit2fa(e.target.checked)} style={{ width: 18, height: 18, cursor: "pointer" }} />
              <span>
                <span style={{ fontSize: "0.85rem", fontWeight: 600, display: "block" }}>Exigir verificación en dos pasos (2FA)</span>
                <span style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>Los usuarios de esta organización deberán configurar 2FA en su siguiente inicio de sesión.</span>
              </span>
            </label>

            <DomicilioFields ccaa={editCcaa} municipio={editMunicipio}
              onChange={(ccaa, municipio) => { setEditCcaa(ccaa); setEditMunicipio(municipio); }} />

            <div>
              <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Notas internas (HelixScan)</label>
              <textarea style={{ ...input, resize: "vertical", minHeight: 80 }} value={editNotes} onChange={e => setEditNotes(e.target.value)} />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              {/* Contrato con Italfarmaco, punto 12: entrega de todos sus datos (10 días si la piden, 30 al terminar) */}
              <button style={{ ...btn("ghost"), marginRight: "auto" }}
                onClick={() => api.download(`/export/organization?org_id=${org.id}`, `helixscan_${org.slug}.zip`).catch(() => {})}>
                Exportar todos sus datos (ZIP)
              </button>
              <button style={btn("ghost")} onClick={onClose}>Cerrar</button>
              <button style={btn()} onClick={saveChanges} disabled={saving}>{saving ? "Guardando…" : "Guardar cambios"}</button>
            </div>
          </div>
        )}

        {/* Tab: Usuarios */}
        {activeTab === "usuarios" && (
          <div>
            {usersLoading ? (
              <p style={{ color: "var(--clr-text3)", textAlign: "center", padding: "1.5rem" }}>Cargando…</p>
            ) : users.length === 0 ? (
              <p style={{ color: "var(--clr-text3)", textAlign: "center", padding: "1.5rem" }}>No hay usuarios en esta organización.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {users.map(u => (
                  <div key={u.id} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "0.75rem 1rem",
                    border: "1px solid var(--clr-border2)",
                    borderRadius: 12,
                    gap: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 8 }}>
                        {u.first_name || u.last_name ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() : u.email}
                        {!u.is_active && <StateDot active={false} label="Inactivo" />}
                      </div>
                      {(u.first_name || u.last_name) && (
                        <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)" }}>{u.email}</div>
                      )}
                      <div style={{ fontSize: "0.72rem", color: "var(--clr-text3)", marginTop: 2 }}>
                        <span style={{ textTransform: "capitalize" }}>{u.role}</span>
                        {u.last_login_at && (
                          <span> · último acceso {new Date(u.last_login_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}</span>
                        )}
                      </div>
                    </div>
                    <button
                      style={{ ...btn("ghost"), whiteSpace: "nowrap", opacity: impersonatingId === u.id ? 0.6 : 1 }}
                      disabled={!u.is_active || impersonatingId !== null}
                      onClick={() => handleImpersonate(u)}
                    >
                      {impersonatingId === u.id ? "Entrando…" : "Entrar como"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab: Branding */}
        {activeTab === "branding" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {brandError && <p style={{ margin: 0, color: "#ef4444", fontSize: "0.82rem" }}>{brandError}</p>}
            {brandSuccess && (
              <p style={{ margin: 0, fontSize: "0.82rem", fontWeight: 500, color: "var(--clr-text2)", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: 3, background: "#22c55e", display: "inline-block", flexShrink: 0 }} />
                Guardado correctamente
              </p>
            )}

            {/* Logo */}
            <div>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text2)", marginBottom: 10 }}>LOGO</div>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div
                  onDrop={e => { e.preventDefault(); setBrandDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleBrandFile(f); }}
                  onDragOver={e => { e.preventDefault(); setBrandDragOver(true); }}
                  onDragLeave={() => setBrandDragOver(false)}
                  onClick={() => brandFileRef.current?.click()}
                  style={{
                    width: 180, height: 80, borderRadius: 12,
                    border: `2px dashed ${brandDragOver ? "var(--clr-org-primary)" : "var(--clr-border)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", background: brandDragOver ? "var(--clr-hover)" : "var(--clr-surface2)",
                    overflow: "hidden", flexShrink: 0,
                  }}
                >
                  {brandLogoPreview
                    ? <img src={brandLogoPreview} alt="logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", padding: 8 }} />
                    : <span style={{ fontSize: "0.75rem", color: "var(--clr-text4)", textAlign: "center", padding: "0 8px" }}>Arrastra o clic<br />para subir logo</span>
                  }
                </div>
                <input ref={brandFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => e.target.files?.[0] && handleBrandFile(e.target.files[0])} />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {brandLogoFile && <span style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>{brandLogoFile.name}</span>}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button style={btn("ghost")} onClick={() => brandFileRef.current?.click()}>
                      {brandLogoPreview ? "Cambiar" : "Seleccionar"}
                    </button>
                    {brandLogoFile && (
                      <button style={btn()} onClick={uploadBrandLogo} disabled={brandUploading}>
                        {brandUploading ? "Subiendo…" : "Guardar logo"}
                      </button>
                    )}
                    {brandLogoUrl && !brandLogoFile && (
                      <button style={btn("danger")} onClick={deleteBrandLogo} disabled={brandSaving}>Eliminar</button>
                    )}
                  </div>
                  <p style={{ margin: 0, fontSize: "0.72rem", color: "var(--clr-text4)" }}>PNG, JPG, WebP o SVG · máx. 2 MB</p>
                </div>
              </div>
            </div>

            {/* Color */}
            <div>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text2)", marginBottom: 10 }}>COLOR PRINCIPAL</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => setBrandColor(c)} style={{
                    width: 32, height: 32, borderRadius: 8, border: brandColor === c ? "3px solid var(--clr-text)" : "2px solid transparent",
                    background: c, cursor: "pointer", outline: "none", transform: brandColor === c ? "scale(1.15)" : "scale(1)", transition: "transform 0.1s",
                  }} title={c} />
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="color" value={brandColor} onChange={e => setBrandColor(e.target.value)}
                  style={{ width: 40, height: 34, borderRadius: 8, border: "1px solid var(--clr-border)", cursor: "pointer", padding: 2 }} />
                <input type="text" value={brandColor}
                  onChange={e => { const v = e.target.value; if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setBrandColor(v); }}
                  style={{ width: 90, padding: "0.4rem 0.75rem", borderRadius: 8, border: "1px solid var(--clr-border)", background: "var(--clr-input-bg)", color: "var(--clr-text)", fontSize: "0.85rem", fontFamily: "monospace", outline: "none" }} />
                {/* Mini preview */}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ width: 60, height: 28, borderRadius: 8, background: brandColor }} />
                  
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button style={btn("ghost")} onClick={onClose}>Cerrar</button>
              <button style={btn()} onClick={saveBrandColor} disabled={brandSaving || !/^#[0-9A-Fa-f]{6}$/.test(brandColor)}>
                {brandSaving ? "Guardando…" : "Guardar color"}
              </button>
            </div>
          </div>
        )}

        {/* Tab: Setup Portfolio */}
        {activeTab === "portfolio" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--clr-text2)", lineHeight: 1.6 }}>
              Introduce los nombres de laboratorio <strong>tal como aparecen en CIMA</strong>. El sistema buscará todos sus medicamentos en el catálogo local y los añadirá al portfolio de esta organización.
            </p>

            {/* Input para añadir labs */}
            <div>
              <label style={{ fontSize: "0.78rem", color: "var(--clr-text2)", display: "block", marginBottom: 4 }}>Nombre de laboratorio (en CIMA)</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  style={{ ...input }}
                  value={labInput}
                  onChange={e => setLabInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addLab()}
                  placeholder='ej: "Almirall S.A." o "Almirall Prodesfarma"'
                />
                <button style={{ ...btn("ghost"), whiteSpace: "nowrap" }} onClick={addLab}>+ Añadir</button>
              </div>
            </div>

            {/* Lista de labs a buscar */}
            {labNames.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {labNames.map(lab => (
                  <span key={lab} style={{ background: "#0891b218", color: "#0891b2", padding: "3px 10px", borderRadius: 20, fontSize: "0.78rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    {lab}
                    <button onClick={() => setLabNames(l => l.filter(x => x !== lab))} style={{ background: "none", border: "none", cursor: "pointer", color: "#0891b2", fontSize: "0.85rem", padding: 0, lineHeight: 1 }}>✕</button>
                  </span>
                ))}
              </div>
            )}

            {setupError && <p style={{ color: "#ef4444", fontSize: "0.8rem", margin: 0 }}>{setupError}</p>}

            {/* Resultado */}
            {setupResult && (
              <div style={{ background: "#0891b212", border: "1px solid #0891b230", borderRadius: 12, padding: "1rem 1.25rem" }}>
                <div style={{ fontWeight: 700, color: "#0891b2", fontSize: "0.9rem", marginBottom: 8 }}>Portfolio actualizado</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: "0.82rem" }}>
                  <span style={{ color: "var(--clr-text2)" }}>Productos cargados:</span>
                  <strong>{setupResult.loaded}</strong>
                  <span style={{ color: "var(--clr-text2)" }}>Ya existían:</span>
                  <strong>{setupResult.skipped}</strong>
                  <span style={{ color: "var(--clr-text2)" }}>Catálogo CIMA:</span>
                  <strong>{setupResult.catalog_size.toLocaleString()} medicamentos</strong>
                </div>
                {setupResult.not_found_in_catalog.length > 0 && (
                  <p style={{ margin: "8px 0 0", fontSize: "0.78rem", color: "#f59e0b" }}>
                    No encontrados en catálogo: {setupResult.not_found_in_catalog.join(", ")}
                  </p>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button style={btn("ghost")} onClick={onClose}>Cerrar</button>
              <button style={btn()} onClick={runSetup} disabled={setupLoading || !labNames.length}>
                {setupLoading ? "Cargando portfolio…" : "Cargar portfolio desde CIMA"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

export default function SuperAdminPage() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [managing, setManaging] = useState<Organization | null>(null);

  const load = async () => {
    setLoading(true);
    try { setOrgs(await api.get<Organization[]>("/organizations")); }
    catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // KPIs
  const total  = orgs.length;
  const active = orgs.filter(o => o.is_active).length;
  const byPlan = { core: 0, pro: 0, ultra: 0 };
  orgs.forEach(o => { if (o.plan in byPlan) byPlan[o.plan as keyof typeof byPlan]++; });

  return (
    <div className="page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700 }}>Organizaciones</h1>
          <p style={{ margin: "4px 0 0", fontSize: "0.85rem", color: "var(--clr-text3)" }}>
            Gestión global de organizaciones y suscripciones
          </p>
        </div>
        <button style={btn()} onClick={() => setShowCreate(true)}>+ Nueva organización</button>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: "1.5rem" }}>
        {[
          { label: "Organizaciones", value: total, color: "var(--clr-text)" },
          { label: "Activas", value: active, color: "#22c55e" },
          { label: "Core", value: byPlan.core, color: PLAN_STYLE.core.color },
          { label: "Pro", value: byPlan.pro, color: PLAN_STYLE.pro.color },
          { label: "Ultra", value: byPlan.ultra, color: PLAN_STYLE.ultra.color },
        ].map(k => (
          <div key={k.label} style={card}>
            <div style={{ fontSize: "0.72rem", color: "var(--clr-text3)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>{k.label}</div>
            <div style={{ fontSize: "1.8rem", fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabla de organizaciones */}
      <div style={card}>
        <h2 style={{ margin: "0 0 1rem", fontSize: "0.95rem", fontWeight: 600 }}>Organizaciones</h2>

        {loading ? (
          <p style={{ color: "var(--clr-text3)", textAlign: "center", padding: "2rem" }}>Cargando…</p>
        ) : orgs.length === 0 ? (
          <p style={{ color: "var(--clr-text3)", textAlign: "center", padding: "2rem" }}>
            No hay organizaciones. Crea la primera con el botón de arriba.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--clr-border)" }}>
                  {["Organización", "Plan", "Usuarios", "Estado", "Contacto", "Creada", ""].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontSize: "0.72rem", fontWeight: 600, color: "var(--clr-text3)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orgs.map(org => (
                  <tr key={org.id} style={{ borderBottom: "1px solid var(--clr-border2)", transition: "background 0.1s" }}
                    onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = "var(--clr-surface2)"}
                    onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = "transparent"}
                  >
                    <td style={{ padding: "0.875rem 0.75rem" }}>
                      <div style={{ fontWeight: 600 }}>{org.name}</div>
                      <div style={{ fontSize: "0.72rem", color: "var(--clr-text3)" }}>/{org.slug}</div>
                    </td>
                    <td style={{ padding: "0.875rem 0.75rem" }}>
                      <PlanBadge plan={org.plan} />
                    </td>
                    <td style={{ padding: "0.875rem 0.75rem" }}>
                      <span style={{ fontWeight: 600 }}>{org.user_count}</span>
                      <span style={{ color: "var(--clr-text3)" }}> / {org.max_users}</span>
                    </td>
                    <td style={{ padding: "0.875rem 0.75rem" }}>
                      <span style={{
                        background: org.is_active ? "#22c55e20" : "#ef444420",
                        color: org.is_active ? "#22c55e" : "#ef4444",
                        padding: "2px 10px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 700
                      }}>
                        {org.is_active ? "Activa" : "Inactiva"}
                      </span>
                    </td>
                    <td style={{ padding: "0.875rem 0.75rem", color: "var(--clr-text2)" }}>
                      <div>{org.contact_name || "—"}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>{org.contact_email || ""}</div>
                    </td>
                    <td style={{ padding: "0.875rem 0.75rem", color: "var(--clr-text3)", fontSize: "0.78rem" }}>
                      {new Date(org.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td style={{ padding: "0.875rem 0.75rem" }}>
                      <RowActionsMenu actions={[
                        { label: "Gestionar", icon: <IconOpen />, onClick: () => setManaging(org) },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateOrgModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
      {managing && (
        <ManageOrgPanel org={managing} onClose={() => setManaging(null)} onUpdated={() => { setManaging(null); load(); }} />
      )}
    </div>
  );
}
